import {
  isObject,
  isNumber,
  isString,
  isDateString,
  isArray,
} from 'utils/typeUtils';
import { convertUTCStringIntoJSTString } from 'utils/dateUtils';

/**
 * お知らせの重要度
 */
export const LEVELS = {
  HIGH: 'high',
  NORMAL: 'normal',
} as const;

/**
 * お知らせの重要度のコード値
 */
export type Level = typeof LEVELS[keyof typeof LEVELS];

/**
 * 配信対象顧客
 *
 * all: すべての会社に表示
 * include: targets に指定された会社にのみ表示
 * exclude: targets に指定された会社以外にのみ表示
 */
export const CONDITIONS = {
  ALL: 'all',
  INCLUDE: 'include',
  EXCLUDE: 'exclude',
} as const;

/**
 * 配信対象顧客の型
 */
export type Condition = typeof CONDITIONS[keyof typeof CONDITIONS];

/**
 * 配信対象顧客の表示名
 */
const CONDITION_NAMES = {
  [CONDITIONS.ALL]: '全顧客',
  [CONDITIONS.INCLUDE]: '選択された顧客にのみ表示する',
  [CONDITIONS.EXCLUDE]: '選択された顧客以外に表示する',
} as const;

/**
 * 配信対象顧客の表示名を生成
 * @param condition 配信対象顧客のコード値（例: all）
 * @returns 配信対象顧客の表示名（例: 全顧客）
 */
export const createConditionName = (condition: Condition): string =>
  CONDITION_NAMES[condition];

/**
 * お知らせの内容 from api
 */
export type OriginalNotification = {
  id: number;
  title: string;
  message: string;
  level: Level;
  startsAt: string;
  endsAt: string;
  condition: Condition;
  targets: number[];
};

/**
 * お知らせ一覧取得 API のレスポンス
 */
export type FetchNotificationsApiResponse = {
  currentPage: number;
  lastPage: number;
  totalCount: number;
  notifications: OriginalNotification[];
};

/**
 * 表示状態
 */
export const DISPLAY_STATUS = {
  /** 未表示 */
  TO_BE_DISPLAYED: 0,
  /** 表示中 */
  DISPLAYING: 1,
  /** 表示終了 */
  END_OF_DISPLAY: 2,
} as const;

/**
 * 表示状態のコード値
 */
export type DisplayStatus = typeof DISPLAY_STATUS[keyof typeof DISPLAY_STATUS];

/**
 * お知らせ一覧の要素
 */
export type Notification = Omit<OriginalNotification, 'level'> & {
  /** level を boolean 化した値 */
  isHigh: boolean;
  /** level の画面表示用の値 */
  levelName: string;
  /** お知らせの表示状態 */
  displayStatus: DisplayStatus;
};

/**
 * レベルの表示名生成
 */
const createLevelName = (level: Level): string =>
  level === LEVELS.HIGH ? '重要' : '-';

/**
 * 表示ステータス生成
 */
const createDisplayStatus = (
  startsAt: string,
  endsAt: string,
  current: string,
): DisplayStatus => {
  const startsAtDate = new Date(startsAt);
  const endsAtDate = new Date(endsAt);
  const currentDate = new Date(current);

  if (currentDate < startsAtDate) {
    return DISPLAY_STATUS.TO_BE_DISPLAYED;
  }
  if (currentDate > endsAtDate) {
    return DISPLAY_STATUS.END_OF_DISPLAY;
  }

  return DISPLAY_STATUS.DISPLAYING;
};

/**
 * お知らせ一覧の要素
 */
export const createNotification = (
  on: OriginalNotification,
  current: string,
): Notification => ({
  id: on.id,
  title: on.title,
  message: on.message,
  condition: on.condition,
  targets: on.targets,
  isHigh: on.level === LEVELS.HIGH,
  levelName: createLevelName(on.level),
  startsAt: convertUTCStringIntoJSTString(on.startsAt),
  endsAt: convertUTCStringIntoJSTString(on.endsAt),
  displayStatus: createDisplayStatus(on.startsAt, on.endsAt, current),
});

/**
 * 重要度コード値のユーザ定義型ガード
 */
const isLevelCode = (arg: unknown): arg is Level => {
  const level = arg as Level;
  const levels = Object.values(LEVELS);

  return levels.includes(level);
};
/**
 * 表示条件コード値のユーザ定義型ガード
 */
const isConditionCode = (arg: unknown): arg is Condition => {
  const conditions = Object.values(CONDITIONS);

  return conditions.includes(arg as Condition);
};
/**
 * お知らせ model from api のユーザ定義型ガード
 */
export const isOriginalNotification = (
  arg: unknown,
): arg is OriginalNotification => {
  const a = arg as OriginalNotification;

  return (
    isObject(a) &&
    isNumber(a.id) &&
    isString(a.title) &&
    isString(a.message) &&
    isLevelCode(a.level) &&
    isDateString(a.startsAt) &&
    isDateString(a.endsAt) &&
    isConditionCode(a.condition) &&
    isArray(a.targets, isNumber)
  );
};

/**
 * お知らせ model from api の一覧のユーザ定義型ガード
 */
export const isOriginalNotifications = (
  arg: unknown,
): arg is OriginalNotification[] => isArray(arg, isOriginalNotification);

/**
 * お知らせ一覧取得 API のレスポンスのユーザ定義型ガード
 */
export const isFetchNotificationApiResponse = (
  arg: unknown,
): arg is FetchNotificationsApiResponse => {
  const a = arg as FetchNotificationsApiResponse;

  return (
    typeof a === 'object' &&
    a !== null &&
    isNumber(a.currentPage) &&
    isNumber(a.lastPage) &&
    isNumber(a.totalCount) &&
    isOriginalNotifications(a.notifications)
  );
};
